'use client';
import { makeStyles, mergeClasses } from '@griffel/react';
import { tokens, typographyStyles } from '@fluentui/react-theme';
import { color as d3Color } from 'd3-color';
export const DEFAULT_ANNOTATION_BACKGROUND_OPACITY = 0.8;
export const DEFAULT_ANNOTATION_PADDING = '4px 8px';
export const DEFAULT_CONNECTOR_START_PADDING = 12;
export const DEFAULT_CONNECTOR_END_PADDING = 0;
export const DEFAULT_CONNECTOR_STROKE_WIDTH = 2;
export const DEFAULT_CONNECTOR_ARROW = 'end';
export const applyOpacityToColor = (color, opacity, options)=>{
    if (!color) {
        return undefined;
    }
    const parsed = d3Color(color);
    if (!parsed) {
        return color;
    }
    const originalOpacity = typeof parsed.opacity === 'number' ? parsed.opacity : 1;
    var _options_preserveOriginalOpacity;
    const preserveOriginalOpacity = (_options_preserveOriginalOpacity = options === null || options === void 0 ? void 0 : options.preserveOriginalOpacity) !== null && _options_preserveOriginalOpacity !== void 0 ? _options_preserveOriginalOpacity : true;
    if (preserveOriginalOpacity && originalOpacity < 1) {
        return parsed.toString();
    }
    parsed.opacity = Math.max(0, Math.min(1, opacity));
    return parsed.toString();
};
/**
 * Get default annotation background color with opacity applied
 * Updated for v9 - uses tokens instead of theme
 */ export const getDefaultAnnotationBackgroundColor = ()=>applyOpacityToColor(tokens.colorNeutralBackground1, DEFAULT_ANNOTATION_BACKGROUND_OPACITY);
/**
 * Get default connector stroke color
 * Updated for v9 - uses tokens instead of theme
 */ export const getDefaultConnectorStrokeColor = ()=>tokens.colorNeutralForeground1;
/**
 * @internal
 */ export const chartAnnotationLayerClassNames = {
    root: 'fui-chartAnnotationLayer__root',
    annotation: 'fui-chartAnnotationLayer__annotation',
    annotationNoDefaults: 'fui-chartAnnotationLayer__annotationNoDefaults',
    connectorLayer: 'fui-chartAnnotationLayer__connectorLayer',
    measurement: 'fui-chartAnnotationLayer__measurement',
    annotationContent: 'fui-chartAnnotationLayer__annotationContent',
    annotationForeignObject: 'fui-chartAnnotationLayer__annotationForeignObject',
    annotationContentInteractive: 'fui-chartAnnotationLayer__annotationContentInteractive',
    annotationForeignObjectInteractive: 'fui-chartAnnotationLayer__annotationForeignObjectInteractive',
    connectorGroup: 'fui-chartAnnotationLayer__connectorGroup'
};
/**
 * Base Styles
 */ const annotationBaseStyles = {
    ...typographyStyles.caption1,
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: tokens.colorNeutralForeground1,
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
    zIndex: 2
};
const useStyles = makeStyles({
    root: {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: 1
    },
    annotation: {
        ...annotationBaseStyles,
        boxShadow: tokens.shadow16,
        border: `1px solid ${tokens.colorNeutralStroke1}`
    },
    annotationNoDefaults: {
        ...annotationBaseStyles
    },
    connectorLayer: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible'
    },
    measurement: {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none'
    },
    annotationContent: {
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    annotationForeignObject: {
        overflow: 'visible',
        pointerEvents: 'none'
    },
    annotationContentInteractive: {
        pointerEvents: 'auto'
    },
    annotationForeignObjectInteractive: {
        pointerEvents: 'auto'
    },
    connectorGroup: {
        pointerEvents: 'none'
    }
});
/**
 * Apply styling to the ChartAnnotationLayer slots based on the state
 */ export const useChartAnnotationLayerStyles = (props)=>{
    var _props_styles, _props_styles1, _props_styles2, _props_styles3, _props_styles4, _props_styles5, _props_styles6, _props_styles7, _props_styles8;
    const baseStyles = useStyles();
    return {
        root: mergeClasses(chartAnnotationLayerClassNames.root, baseStyles.root, props.className, (_props_styles = props.styles) === null || _props_styles === void 0 ? void 0 : _props_styles.root),
        annotation: mergeClasses(chartAnnotationLayerClassNames.annotation, baseStyles.annotation, (_props_styles1 = props.styles) === null || _props_styles1 === void 0 ? void 0 : _props_styles1.annotation),
        connectorLayer: mergeClasses(chartAnnotationLayerClassNames.connectorLayer, baseStyles.connectorLayer, (_props_styles2 = props.styles) === null || _props_styles2 === void 0 ? void 0 : _props_styles2.connectorLayer),
        measurement: mergeClasses(chartAnnotationLayerClassNames.measurement, baseStyles.measurement, (_props_styles3 = props.styles) === null || _props_styles3 === void 0 ? void 0 : _props_styles3.measurement),
        annotationContent: mergeClasses(chartAnnotationLayerClassNames.annotationContent, baseStyles.annotationContent, (_props_styles4 = props.styles) === null || _props_styles4 === void 0 ? void 0 : _props_styles4.annotationContent),
        annotationForeignObject: mergeClasses(chartAnnotationLayerClassNames.annotationForeignObject, baseStyles.annotationForeignObject, (_props_styles5 = props.styles) === null || _props_styles5 === void 0 ? void 0 : _props_styles5.annotationForeignObject),
        annotationContentInteractive: mergeClasses(chartAnnotationLayerClassNames.annotationContentInteractive, baseStyles.annotationContentInteractive, (_props_styles6 = props.styles) === null || _props_styles6 === void 0 ? void 0 : _props_styles6.annotationContentInteractive),
        annotationForeignObjectInteractive: mergeClasses(chartAnnotationLayerClassNames.annotationForeignObjectInteractive, baseStyles.annotationForeignObjectInteractive, (_props_styles7 = props.styles) === null || _props_styles7 === void 0 ? void 0 : _props_styles7.annotationForeignObjectInteractive),
        connectorGroup: mergeClasses(chartAnnotationLayerClassNames.connectorGroup, baseStyles.connectorGroup, (_props_styles8 = props.styles) === null || _props_styles8 === void 0 ? void 0 : _props_styles8.connectorGroup)
    };
};
